import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { AVAILABLE_CLASSES } from '../constants/info';
import offerBallet from '../assets/img/offer-ballet.jpeg';
import offerCapoeira from '../assets/img/offer-capoeira.png';
import offerJujitsu from '../assets/img/offer-jujitsu.jpeg';
import offerFutsal from '../assets/img/offer-futsal.jpeg';
import offerMusic from '../assets/img/offer-music.jpeg';
import kidsDancing2 from '../assets/img/kids-dancing-2.jpg';
import '../assets/css/Offer.css';

const EXTRA_ACTIVITIES = [
  {
    name: 'Ballet',
    description: 'Expressão corporal, disciplina e coordenação por meio da dança clássica.',
    image: offerBallet
  },
  {
    name: 'Capoeira',
    description: 'Atividade cultural e esportiva que combina luta, dança e música.',
    image: offerCapoeira
  },
  {
    name: 'Jiu-jitsu',
    description: 'Arte marcial voltada para autodefesa, disciplina e controle corporal.',
    image: offerJujitsu
  },
  {
    name: 'Futsal',
    description: 'Versão indoor do futebol que estimula agilidade, estratégia e espírito de equipe.',
    image: offerFutsal
  },
  {
    name: 'Instrumentos musicais',
    description: 'Desenvolvimento musical e cognitivo através de aulas práticas com diversos instrumentos.',
    image: offerMusic
  }
];

const TABS = [
  { key: 'classes', label: 'Valências', color: '#3498db' },
  { key: 'activities', label: 'Atividades Extracurriculares', color: '#e91e63' },
];

const handleRipple = (e) => {
  const button = e.currentTarget;
  const circle = document.createElement("span");
  const diameter = Math.max(button.clientWidth, button.clientHeight);
  const radius = diameter / 2;

  circle.style.width = circle.style.height = `${diameter}px`;
  circle.style.left = `${e.clientX - (button.offsetLeft + radius)}px`;
  circle.style.top = `${e.clientY - (button.offsetTop + radius)}px`;
  circle.classList.add("ripple");

  const ripple = button.getElementsByClassName("ripple")[0];

  if (ripple) {
    ripple.remove();
  }

  button.appendChild(circle);
};

const getKindergartenColor = (index) => {
  const colors = ['#e91e63', '#ff9800', '#4caf50', '#ffeb3b', '#3498db', '#f44336'];
  return colors[index % colors.length];
};

const getPrimaryColor = (index) => {
  const colors = ['#3498db', '#4caf50', '#e91e63', '#ff9800', '#ffeb3b', '#f44336'];
  return colors[index % colors.length];
};

const getSecundarioColor = (index) => {
  const colors = ['#9c27b0', '#3498db', '#4caf50', '#ff9800', '#e91e63', '#f44336'];
  return colors[index % colors.length];
};

const Offer = () => {
  const [activeTab, setActiveTab] = useState('classes');
  const phrases = [
    "Onde cada criança é uma estrela.",
    "A educação que transforma.",
    "Preparando o futuro com carinho."
  ];
  const [currentPhrase, setCurrentPhrase] = React.useState('');
  const [phraseIndex, setPhraseIndex] = React.useState(0);
  const [charIndex, setCharIndex] = React.useState(0);
  const [isDeleting, setIsDeleting] = React.useState(false);
  const heroSubtitleRef = React.useRef(null);

  React.useEffect(() => {
    const typeSpeed = 70;
    const deleteSpeed = 50;
    const delayBetweenPhrases = 1500;
    let typingTimeout;
    const handleTyping = () => {
      const current = phrases[phraseIndex];
      if (!isDeleting) {
        setCurrentPhrase(current.substring(0, charIndex + 1));
        setCharIndex(prev => prev + 1);
        if (charIndex + 1 === current.length) {
          setIsDeleting(true);
          typingTimeout = setTimeout(() => { }, delayBetweenPhrases);
        }
      } else {
        setCurrentPhrase(current.substring(0, charIndex - 1));
        setCharIndex(prev => prev - 1);
        if (charIndex - 1 === 0) {
          setIsDeleting(false);
          setPhraseIndex((prev) => (prev + 1) % phrases.length);
        }
      }
    };
    typingTimeout = setTimeout(handleTyping, isDeleting ? deleteSpeed : typeSpeed);
    return () => clearTimeout(typingTimeout);
  }, [charIndex, isDeleting, phraseIndex, phrases]);

  // Mover renderContent para dentro do componente
  const renderContent = () => {
    if (activeTab === 'classes') {
      return (
        <div className="container">
          <div className="section-o-intro">
            <h2 className="section-o-title" data-accent-color="#3498db">Infantário</h2>
            <p>
              Para crianças de 0 a 5 anos, nosso infantário oferece um ambiente seguro e acolhedor, 
              focado na ludicidade e no desenvolvimento integral através de atividades criativas e educativas.
            </p>
          </div>

          <div className="row g-4 mb-5">
            {AVAILABLE_CLASSES.kindergarten.map((turma, index) => (
              <div className="col-md-6 col-lg-4" key={`kindergarten-${turma}`}>
                <motion.div 
                  className="class-card"
                  data-accent-color={getKindergartenColor(index)}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1, duration: 0.5 }}
                  whileHover={{ y: -8 }}
                >
                  <span>{turma}</span>
                </motion.div>
              </div>
            ))}
          </div>

          <div className="section-o-intro">
            <h2 className="section-o-title" data-accent-color="#4caf50">Ensino Primário</h2>
            <p>
              Nosso ensino primário, do 1º ao 6º ano, foca no desenvolvimento acadêmico sólido, 
              pensamento crítico e habilidades socioemocionais, preparando os alunos para os desafios futuros.
            </p>
          </div>

          <div className="row g-4 mb-5">
            {AVAILABLE_CLASSES.primary.map((classe, index) => (
              <div className="col-md-6 col-lg-4" key={`primary-${classe}`}>
                <motion.div
                  className="class-card"
                  data-accent-color={getPrimaryColor(index)}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1, duration: 0.5 }}
                  whileHover={{ y: -8 }}
                >
                  <span>{classe}</span>
                </motion.div>
              </div>
            ))}
          </div>

          <div className="section-o-intro">
            <h2 className="section-o-title" data-accent-color="#9c27b0">Iº Ciclo</h2>
            <p>
              Nosso Iº Ciclo, 7ª classe, dá continuidade ao percurso académico
              com foco no aprofundamento do conhecimento, na autonomia e na preparação para os ciclos seguintes.
            </p>
          </div>

          <div className="row g-4">
            {AVAILABLE_CLASSES.secondary.map((classe, index) => (
              <div className="col-md-6 col-lg-4" key={`secondary-${classe}`}>
                <motion.div
                  className="class-card"
                  data-accent-color={getSecundarioColor(index)}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1, duration: 0.5 }}
                  whileHover={{ y: -8 }}
                >
                  <span>{classe}</span>
                </motion.div>
              </div>
            ))}
          </div>
        </div>
      );
    } else if (activeTab === 'activities') {
      return (
        <div className="container">
          <div className="section-o-intro">
            <h2 className="section-o-title" data-accent-color="#e91e63">Atividades Extracurriculares</h2>
            <p>
              Enriquecemos o currículo com atividades extracurriculares que estimulam talentos e paixões, 
              promovendo um desenvolvimento holístico e diversificado para nossos alunos.
            </p>
          </div>

          <div className="row g-4">
            {EXTRA_ACTIVITIES.map((activity, index) => (
              <div className="col-md-6 col-lg-4" key={activity.name}>
                <motion.div 
                  className="activity-o-card"
                  data-accent-color={index % 2 === 0 ? '#e91e63' : '#3498db'}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1, duration: 0.5 }}
                  whileHover={{ y: -8 }}
                >
                  <img 
                    src={activity.image} 
                    alt={activity.name} 
                    className="activity-o-image" 
                  />
                  <div className="activity-o-content">
                    <h3 className="activity-o-title">{activity.name}</h3>
                    <p className="activity-o-description">{activity.description}</p>
                  </div>
                </motion.div>
              </div>
            ))}
          </div>
        </div>
      );
    }
    return null;
  }

  return (
    <>
      <motion.section
        className="hero-o-section"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, ease: "easeOut" }}
      >
        <div className="hero-o-background-image"></div>
        <div className="hero-o-overlay"></div>
        <div className="hero-o-decoration-circles">
          <div className="hero-o-circle hero-o-circle-1"></div>
          <div className="hero-o-circle hero-o-circle-2"></div>
          <div className="hero-o-circle hero-o-circle-3"></div>
          <motion.div 
            className="floating-shape shape-star"
            animate={{ y: [0, -15, 0], rotate: [0, 10, 0] }}
            transition={{ repeat: Infinity, duration: 5, ease: "easeInOut" }}
          >
            <i className="bi bi-star-fill"></i>
          </motion.div>
          <motion.div 
            className="floating-shape shape-circle"
            animate={{ y: [0, 20, 0], x: [0, 10, 0] }}
            transition={{ repeat: Infinity, duration: 7, ease: "easeInOut" }}
          ></motion.div>
        </div>
        <div className="hero-content">
          <motion.h1
            className="hero-title"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2, ease: "easeOut" }}
          >
            <span className="text-gradient">Nossa Oferta</span> <br />
            <span className="text-white">Educativa</span>
          </motion.h1>

          <motion.p 
            className="hero-description"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.5, ease: "easeOut" }}
          >
            No Talatona Kids, cada criança é única e especial. Nossa missão é nutrir talentos, 
            desenvolver habilidades e criar memórias que durarão para toda a vida.
          </motion.p>

          {/**
          <motion.h4
            className="hero-subtitle"
            ref={heroSubtitleRef}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4, ease: "easeOut" }}
          >
            {currentPhrase}
            <span className="blinking-cursor">|</span>
          </motion.h4>
          <motion.div
            className="hero-cta-container"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6, ease: "easeOut" }}
          >
            <a 
              href="/pre-registration" 
              className="hero-cta primary"
              onClick={handleRipple}
            >
              <i className="bi bi-pencil-square me-2"></i>
              Inscreva-se Já!
            </a>
            <a 
              href="/offer" 
              className="hero-cta secondary"
            >
              <i className="bi bi-info-circle me-2"></i>
              Conheça Nossos Programas
            </a>
          </motion.div>
        */}
          <motion.div
            className="hero-stats"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 1, ease: "easeOut" }}
          >
            <motion.div 
              className="hero-stat-item"
              whileHover={{ y: -5, transition: { duration: 0.2 } }}
            >
              <div className="stat-icon-container">
                <i className="bi bi-calendar-check-fill"></i>
              </div>
              <span className="hero-stat-number">10+</span>
              <span className="hero-stat-text">Anos de Experiência</span>
            </motion.div>
            <motion.div 
              className="hero-stat-item"
              whileHover={{ y: -5, transition: { duration: 0.2 } }}
            >
              <div className="stat-icon-container">
                <i className="bi bi-mortarboard-fill"></i>
              </div>
              <span className="hero-stat-number">200+</span>
              <span className="hero-stat-text">Alunos Formados</span>
            </motion.div>
            <motion.div 
              className="hero-stat-item"
              whileHover={{ y: -5, transition: { duration: 0.2 } }}
            >
              <div className="stat-icon-container">
                <i className="bi bi-emoji-smile-fill"></i>
              </div>
              <span className="hero-stat-number">98%</span>
              <span className="hero-stat-text">Pais Satisfeitos</span>
            </motion.div>
          </motion.div>
        </div>
        <motion.div 
          className="hero-visual-container"
          initial={{ opacity: 0, x: 50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8, delay: 0.5, ease: "easeOut" }}
        >
          <div className="hero-visual">
            <img 
              src={kidsDancing2}
              alt="Crianças dançando no Talatona Kids"
              className="hero-main-image"
            />
            <div className="hero-visual-floating">
              <motion.div 
                className="floating-badge safety"
                initial={{ x: -20, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ duration: 0.5, delay: 1.2 }}
                whileHover={{ y: -5, scale: 1.05, transition: { duration: 0.2 } }}
              >
                <i className="bi bi-shield-check"></i>
                <span>Ambiente Seguro</span>
              </motion.div>
              <motion.div 
                className="floating-badge qualified"
                initial={{ x: 20, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ duration: 0.5, delay: 1.4 }}
                whileHover={{ y: -5, scale: 1.05, transition: { duration: 0.2 } }}
              >
                <i className="bi bi-mortarboard-fill"></i>
                <span>Professores Qualificados</span>
              </motion.div>
              <motion.div 
                className="floating-badge care"
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.5, delay: 1.6 }}
                whileHover={{ y: -5, scale: 1.05, transition: { duration: 0.2 } }}
              >
                <i className="bi bi-heart-fill"></i>
                <span>Carinho e Atenção</span>
              </motion.div>
            </div>
          </div>
        </motion.div>
      </motion.section>

      <section className="section-o-container">
        <div className="tabs-container">
          {TABS.map((tab) => (
            <motion.button
              key={tab.key}
              className={`tab-button ${activeTab === tab.key ? 'active' : ''}`}
              data-accent-color={tab.color}
              onClick={() => setActiveTab(tab.key)}
              onMouseDown={handleRipple}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              {tab.label}
            </motion.button>
          ))}
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.5 }}
          >
            {renderContent()}
          </motion.div>
        </AnimatePresence>
      </section>
    </>
  );
};

export default Offer;